import { useState, useEffect } from 'react';

/**
 * Detects whether the current device primarily uses touch input.
 * Uses the `(pointer: coarse)` media query to distinguish phones/tablets
 * from laptops with touchscreens. Also listens for the first touch event
 * to handle hybrid devices that start with a mouse but switch to touch.
 */
export function useIsTouchDevice(): boolean {
  const [isTouchDevice, setIsTouchDevice] = useState(() => {
    if (typeof window === 'undefined') return false;
    // Primary pointer is coarse (finger) — strongest signal
    return window.matchMedia('(pointer: coarse)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Listen for media query changes (e.g. tablet connecting/disconnecting keyboard)
    const mql = window.matchMedia('(pointer: coarse)');
    const handleChange = (e: MediaQueryListEvent) => {
      setIsTouchDevice(e.matches);
    };
    mql.addEventListener('change', handleChange);

    // Fallback: if the first interaction is a touch, switch to touch mode
    const handleFirstTouch = () => {
      setIsTouchDevice(true);
      window.removeEventListener('touchstart', handleFirstTouch);
    };
    if (!isTouchDevice) {
      window.addEventListener('touchstart', handleFirstTouch, { once: true });
    }

    return () => {
      mql.removeEventListener('change', handleChange);
      window.removeEventListener('touchstart', handleFirstTouch);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return isTouchDevice;
}
